import { JsonFormsCore, JsonSchema, Translator, UISchemaElement } from '@jsonforms/core';
import {
  ProgramContext,
  ProgramMetadata,
  ProgramVersion,
  selectStepsForAudience,
} from '@plentifulapp/plentiful-program-configuration';
import {
  EligibilityEngine,
  EligibilityEvaluation,
  SaveResult,
  WizardEngine,
} from '@plentifulapp/plentiful-wizard-engine';
import { WizardForm } from '@plentifulapp/plentiful-wizard-engine/types/WizardForm';
import { AttestationFacade, AttestationResponse } from '../services/AttestationFacade';
import { languageCode } from '../../types/Languages';
import { testContainsHtml } from '../form/json-forms/renderers/RichTextRenderer.tsx';
import StateProgramsError from './errors/StateProgramsError';
import { debug } from '../../utils/logger';
import { NEIGHBOR_AUDIENCE, neighborProgramContext } from './neighborProgramContext';

/**
 * The bound program context and engine implementations that
 * {@link StateProgramFacade.buildForProgram} composes.
 * ---
 *
 * These are constant in production — the values in
 * {@link defaultStateProgramDependencies}. The bundle exists as a parameter so
 * tests can substitute fakes; only the attestation facade genuinely varies by
 * environment and so stays a separate, required argument.
 */
export interface StateProgramDependencies {
  /**
   * The neighbor-bound program context. Only the two accessors the factory reads
   * are required, so a test fake need not implement the whole context.
   */
  programContext: Pick<ProgramContext, 'getProgramMetadata' | 'getProgramActiveVersion'>;
  WizardEngineFactory: typeof WizardEngine;
  EligibilityEngineFactory: typeof EligibilityEngine;
}

/**
 * Production wiring: the real neighbor-bound program context, engine
 * constructors, and audience projector. Used by default so callers pass only
 * what varies by environment (the attestation facade).
 */
export const defaultStateProgramDependencies: StateProgramDependencies = {
  programContext: neighborProgramContext,
  WizardEngineFactory: WizardEngine,
  EligibilityEngineFactory: EligibilityEngine,
};

/**
 * Encapsulation for dependencies to the generalized state programs.
 * Note that these dependencies are all-or-nothing, so unless all of them are
 * able to be created for a given program, then the program should not be used.
 */
// Export as default class to satisfy ESLint import/prefer-default-export
export default class StateProgramFacade {
  /**
   * Indicates if the last update to the form was a success or not.
   * The handler does get called when step first loads,
   * so this should flip to a real state as soon as the form load.
   */
  private lastStepUpdate?: SaveResult;

  /**
   * Constructs a wrapper for the state program services. Should only be used through the factory.
   * @param programMetadata Metadata that identifies the program and points to the current version.
   * @param programVersion Configuration for the current version of the program being used.
   * @param wizardEngine Engine that manages the current step in a wizard-style form.
   * @param eligibilityEngine Engine that manages the eligibility for a program.
   * @param attestationFacade Client for service calls to read and write attestations.
   */
  private constructor(
    private programMetadata: ProgramMetadata,
    private programVersion: ProgramVersion,
    private wizardEngine: WizardEngine,
    private eligibilityEngine: EligibilityEngine,
    private attestationFacade: AttestationFacade
  ) {}

  /**
   * @returns ID for the program that these service instances support.
   */
  public get id() {
    return this.programMetadata.id;
  }

  /**
   * @returns Human-readable name for the program.
   */
  public get name() {
    return this.programMetadata.name;
  }

  /**
   * @returns Current program version being serviced.
   */
  public get version() {
    return this.programMetadata.activeVersion;
  }

  /**
   * @returns Whether a program is available in a specific state.
   */
  public isAvailableInState(state: string): boolean {
    return this.programMetadata.availabilityCriteria.state === state;
  }

  /**
   * @returns true if last change passed validation and was saved to the Wizard Engine.
   *
   * Note that the cached update is only undefined while the form is still loading,
   * and in this case it will default to assuming that the update is not successful yet.
   * When the form loads, before a user interacts with it, JSON Forms calls the update
   * handler once, which sets this to a real update
   *
   * ({@see StateProgramFormStep.onChange} for defaults on how this first update is treated.)
   *
   * The user impact of this loop and the decision to default to treating it as not
   * valid while the form loads is that if the form renders before data gets populated into it
   * (which this author's eyes can't see but is possible depending on how JSON Forms orders the
   * render and data population) then the button would be disabled until the data loads.
   * It does *not* mean that a valid form would start as disabled, because the state will
   * flip to valid as soon as the form loads and the update gets passed through.
   */
  public get isStepUpdateSuccessful(): boolean {
    return this.lastStepUpdate?.success ?? false;
  }

  /**
   * @returns validation errors from last change for empty array if no errors.
   */
  public get stepValidationErrors() {
    return this.lastStepUpdate?.success ? [] : this.lastStepUpdate?.errors ?? [];
  }

  /**
   * @returns JSON Forms compatible JSON schema for the attestation data on the current step.
   */
  public get stepAttestationSchema(): JsonSchema {
    // convert from the validator's type to the form's type, but should match
    return this.wizardEngine.getCurrentStep().jsonSchema as JsonSchema;
  }

  /**
   * @returns JSON Forms compatible JSON schema for the UI presentation of the step.
   */
  public get stepUiSchema(): UISchemaElement {
    // convert from the validator's type to the form's type
    // (with a layer in between because it doesn't know that they align)
    return this.wizardEngine.getCurrentStep().uiSchema as unknown as UISchemaElement;
  }

  /**
   * @param locale Current user selected locale.
   * @returns translator to use for current step.
   */
  public createStepTranslator(locale: languageCode): Translator {
    return this.wizardEngine.createTranslator(locale, testContainsHtml);
  }

  /**
   * @returns Form data from current step only.
   */
  public get stepAttestationData() {
    return this.wizardEngine.getStepFormData();
  }

  /**
   * @returns Form data from all steps.
   */
  public get completeAttestationData() {
    return this.wizardEngine.getCompleteFormData();
  }

  public changeStepData(state: Pick<JsonFormsCore, 'data' | 'errors'>) {
    // persist if the data passed validation before moving on
    this.lastStepUpdate = this.wizardEngine.saveFormData(state.data);
    return this.lastStepUpdate;
  }

  /**
   * @returns Whether the current step is the last step in the wizard
   */
  public get isLastStep(): boolean {
    return this.wizardEngine.isLastStep();
  }

  /**
   * @returns Whether the current step is the first step in the wizard
   */
  public get isFirstStep(): boolean {
    return this.wizardEngine.isFirstStep();
  }

  /**
   * @returns ID of the current step for tracking step changes
   */
  public get currentStepId(): string {
    return this.wizardEngine.getCurrentStep().id;
  }

  /**
   * Advances to the next step in the wizard
   * @throws StateProgramsError if navigation fails (validation or unexpected errors)
   */
  public goToNextStep(): void {
    const result = this.wizardEngine.goForward(this.stepAttestationData);

    if (!result.success) {
      throw new StateProgramsError(
        `Unable to navigate to next step: ${result.reason || 'unknown reason'}`,
        new Error('Navigation failed'),
        this.id,
        this.version
      );
    }
  }

  /**
   * Navigates to the previous step in the wizard.
   * @throws StateProgramsError if navigation fails
   */
  public goToPreviousStep(): boolean {
    const result = this.wizardEngine.goBack();

    if (!result) {
      throw new StateProgramsError(
        'Unable to navigate to previous step: navigation returned false',
        new Error('Navigation failed'),
        this.id,
        this.version
      );
    }

    return result;
  }

  /**
   * Resets the wizard to the first step by calling goBack() until isFirstStep() returns true
   * @throws Error if navigation fails
   */
  public goToFirstStep(): void {
    // Go back until we reach the first step
    while (!this.wizardEngine.isFirstStep()) {
      // return immediately if already at first step
      this.goToPreviousStep(); // let it throw if navigation fails
    }
  }

  /**
   * Submits an attestation to this program.
   * @param neighborUserId User ID of the neighbor making the attestation.
   * @param visitId Registered visit that the attestations is submitted to.
   * @param providerId
   */
  public async attest(neighborUserId: number, visitId: number, providerId: number): Promise<void> {
    const eligibility: EligibilityEvaluation = this.getEligibility();
    return this.attestationFacade.submit(
      neighborUserId,
      visitId,
      providerId,
      this.programMetadata.id,
      this.programVersion.version,
      this.completeAttestationData,
      eligibility
    );
  }

  /**
   * Gets an attestation for this program.
   * @param visitId Registered visit that the attestations may be associated with.
   */
  public async getAttestation(visitId: number): Promise<AttestationResponse | null> {
    return this.attestationFacade.get(visitId, this.programMetadata.id, this.programVersion.version);
  }

  /**
   * Gets the eligibility for the form data that currently exists for a program.
   */
  public getEligibility(): EligibilityEvaluation {
    return this.eligibilityEngine.isEligible(this.wizardEngine.getCompleteFormData());
  }

  /**
   * Build state program services for the specific program.
   * @param programId The ID of the state program to use.
   * @param attestationFacade Instance of the Attestation Facade for the current environment.
   * @param deps Bundled config/engine implementations; defaults to the real
   *   production wiring ({@link defaultStateProgramDependencies}). Override in
   *   tests to substitute fakes.
   * @returns The encapsulated services, or `null` when the program has no steps
   *   visible to a Neighbor (e.g. a provider-only program) and is withheld.
   */
  public static buildForProgram(
    programId: string,
    attestationFacade: AttestationFacade,
    deps: StateProgramDependencies = defaultStateProgramDependencies
  ): StateProgramFacade | null {
    const { programContext, WizardEngineFactory, EligibilityEngineFactory } = deps;
    const programMetadata = programContext.getProgramMetadata(programId);
    const projected = selectStepsForAudience(
      programContext.getProgramActiveVersion(programId),
      NEIGHBOR_AUDIENCE
    );

    if (!projected) {
      // Provider-only (or otherwise not Neighbor-visible) program: withhold it.
      // `selectStepsForAudience` returns undefined exactly when this audience has
      // no visible steps, never a stepless form. Expected behaviour, not a fault —
      // must not throw into the error boundary.
      debug(`[StateProgramFacade] Withholding ${programId}: no steps for audience "${NEIGHBOR_AUDIENCE}".`);
      return null;
    }

    const wizardEngine = new WizardEngineFactory(projected.wizardForm);
    const eligibilityEngine = new EligibilityEngineFactory(
      projected.eligibilityRuleset,
      projected.wizardForm as WizardForm
    ); // if the wizard engine was created successfully then we know that the wizardForm is of type WizardForm
    return new StateProgramFacade(
      programMetadata,
      projected,
      wizardEngine,
      eligibilityEngine,
      attestationFacade
    );
  }
}
